import { useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import DestinationCard from './DestinationCard';

export type PopularDestinationItem = {
  image: string;
  name: string;
  filterDestination?: string;
  price: number;
  primaryColor: string;
};

const PER_SLIDE = 3;

function chunkDestinations<T>(items: T[], size: number): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < items.length; i += size) {
    out.push(items.slice(i, i + size));
  }
  return out;
}

export default function PopularDestinationsCarousel({ items }: { items: PopularDestinationItem[] }) {
  const slides = useMemo(() => chunkDestinations(items, PER_SLIDE), [items]);
  const [slide, setSlide] = useState(0);

  const canPrev = slide > 0;
  const canNext = slide < slides.length - 1;

  return (
    <div className="relative">
      {/* Desktop & tablette large : 3 cartes / slide + flèches */}
      <div className="hidden md:block">
        <div className="relative px-8 sm:px-12">
          <button
            type="button"
            aria-label="Destinations précédentes"
            disabled={!canPrev}
            onClick={() => setSlide((s) => Math.max(0, s - 1))}
            className="absolute left-0 top-1/2 z-10 -translate-y-1/2 rounded-full border border-gray-200 bg-white p-3 text-gray-800 shadow-md transition hover:bg-gray-50 disabled:pointer-events-none disabled:opacity-35"
          >
            <ChevronLeft className="h-6 w-6" />
          </button>
          <button
            type="button"
            aria-label="Destinations suivantes"
            disabled={!canNext}
            onClick={() => setSlide((s) => Math.min(slides.length - 1, s + 1))}
            className="absolute right-0 top-1/2 z-10 -translate-y-1/2 rounded-full border border-gray-200 bg-white p-3 text-gray-800 shadow-md transition hover:bg-gray-50 disabled:pointer-events-none disabled:opacity-35"
          >
            <ChevronRight className="h-6 w-6" />
          </button>

          <div className="overflow-hidden rounded-xl">
            <div
              className="flex transition-transform duration-500 ease-out will-change-transform"
              style={{ transform: `translateX(-${slide * 100}%)` }}
            >
              {slides.map((chunk, slideIndex) => (
                <div
                  key={slideIndex}
                  className="grid min-w-full shrink-0 grid-cols-3 gap-3 sm:gap-4 lg:gap-6"
                >
                  {chunk.map((dest, j) => (
                    <div key={`${dest.name}-${dest.filterDestination ?? j}`} className="min-w-0">
                      <DestinationCard {...dest} />
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-8 flex justify-center gap-2">
          {slides.map((_, i) => (
            <button
              key={i}
              type="button"
              aria-label={`Voir le groupe ${i + 1}`}
              aria-current={i === slide}
              onClick={() => setSlide(i)}
              className={`h-2.5 rounded-full transition-all ${
                i === slide ? 'w-10 bg-blue-600' : 'w-2.5 bg-gray-300 hover:bg-gray-400'
              }`}
            />
          ))}
        </div>
      </div>

      {/* Mobile : défilement horizontal */}
      <div className="md:hidden -mx-4 flex gap-6 overflow-x-auto scroll-smooth px-4 pb-2 pt-1 [scrollbar-width:thin] snap-x snap-mandatory">
        {items.map((dest, i) => (
          <div
            key={`${dest.name}-${dest.filterDestination ?? i}-m`}
            className="w-[min(82vw,340px)] shrink-0 snap-center"
          >
            <DestinationCard {...dest} />
          </div>
        ))}
      </div>
    </div>
  );
}
